import { ReactElement, useState, useEffect, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { useTyreReferenceSessions, useSessionTyreReference } from '../../hooks/useSessions';
import Spinner from '../../components/Spinner';
import SectionHeader from '../../components/SectionHeader';
import { useEquipmentCatalog, useWheelCircumferenceData } from '../../hooks/useEquipment';
import { useRidersById } from '../../hooks/useRiders';
import TyreDeltaBarChartApex from '../../components/charts/TyreDeltaBarChartApex';
import TyreDeltaScatterChart from '../../components/charts/TyreDeltaScatterChart';
import { SurfaceQuality } from '../../types/SurfaceQuality';
import { computeAxisRange } from '../../utils/chartAxisRange';
import CoordinationTab from './CoordinationTab';
import WeatherTemperaturePlot from '../SessionView/SessionDetails/WeatherTemperaturePlot';
import CrrTempCombinedPlot from '../../components/charts/CrrTempCombinedPlot';
import CrrTempFullPlot from '../../components/charts/CrrTempFullPlot';
import CourseMapPlot from '../../components/charts/CourseMapPlot';
import ElevationProfilePlot from '../../components/charts/ElevationProfilePlot';
import WindSpeedPlot from '../../components/charts/WindSpeedPlot';
import WindDirectionPlot from '../../components/charts/WindDirectionPlot';
import CrrTimeOfDayPlot from '../../components/charts/CrrTimeOfDayPlot';
import CrrRawTimeOfDayPlot from '../../components/charts/CrrRawTimeOfDayPlot';
import CrrDeltaTimeOfDayPlot from '../../components/charts/CrrDeltaTimeOfDayPlot';
import TyreReportWheelCircumferencePlot from '../../components/charts/TyreReportWheelCircumferencePlot';
import TyreCompensationPlot from '../../components/charts/TyreCompensationPlot';
import { HiOutlineDocumentArrowDown, HiOutlineArrowDownTray } from 'react-icons/hi2';
import { executeExportHtml } from '../../utils/printUtils';
import { getTyreReportDataExport } from '../../api/sessions';
import streamlinesLogo from '../../icons/logo-streamlines.png';
import {
  PrintDataTable,
  buildPrintDataRows,
  getStandardCellValue,
} from '../../components/PrintDataTable';
import {
  getPrintDataColumnsTyrePage1,
  PRINT_DATA_COLUMNS_TYRE_PAGE2,
} from '../SessionView/SessionDetails/SessionCharts/tyrePrintColumns';

// Helper function to get tyre name from equipment catalog
const getTyreName = (
  tyreId: string | null | undefined,
  equipmentCatalog: any,
) => {
  if (!tyreId) return 'Unknown Tyre';
  if (
    equipmentCatalog?.tyres
    && Array.isArray(equipmentCatalog.tyres)
  ) {
    const tyre = equipmentCatalog.tyres.find(
      (t: any) => t.id === tyreId,
    );
    if (tyre) {
      return (
        tyre.name
        || tyre.designation
        || `Tyre ${tyreId.slice(0, 8)}`
      );
    }
  }
  return `Tyre ${tyreId.slice(0, 8)}`;
};

// Helper function to convert pressure
const convertPressure = (pressureBar: number, pressureUnit: 'Bar' | 'PSI') => {
  return pressureUnit === 'PSI' ? pressureBar / 0.06895 : pressureBar;
};

// Helper function to get combined tyre name (front/rear or just rear if same)
const getCombinedTyreName = (frontId: string | null | undefined, rearId: string, equipmentCatalog: any) => {
  const rearName = getTyreName(rearId, equipmentCatalog);

  if (!frontId || frontId === rearId) {
    // Same tyre front and rear, or no front tyre specified
    return rearName;
  }

  const frontName = getTyreName(frontId, equipmentCatalog);
  return `${frontName} / ${rearName}`;
};

export default function TyreReports(): ReactElement {
  // Tab state
  type TabType =
    | 'coordination'
    | 'performance'
    | 'temperature'
    | 'report';
  const [activeTab, setActiveTab] = useState<TabType>(
    () => {
      const stored = localStorage.getItem(
        'tyreReports_activeTab',
      ) as TabType;
      return stored || 'performance';
    },
  );

  // Load settings from localStorage
  const [selectedSessionId, setSelectedSessionId] = useState<string>(() => {
    return localStorage.getItem('multiReports_selectedSessionId') || '';
  });
  const [pressureUnit, setPressureUnit] = useState<'Bar' | 'PSI'>(() => {
    return (localStorage.getItem('multiReports_pressureUnit') as 'Bar' | 'PSI') || 'Bar';
  });
  const [deltaAverage, setDeltaAverage] = useState<'Constant' | 'Moving'>(() => {
    return (localStorage.getItem('multiReports_deltaAverage') as 'Constant' | 'Moving') || 'Constant';
  });
  const [speedScale, setSpeedScale] = useState<number>(1);
  const [selectedTyreFilter, setSelectedTyreFilter] = useState<string>(() => {
    return localStorage.getItem('multiReports_selectedTyreFilter') || 'All';
  });
  const [chartType, setChartType] = useState<'bar' | 'scatter'>(() => {
    return (localStorage.getItem('multiReports_chartType') as 'bar' | 'scatter') || 'bar';
  });
  const [scatterGroupBy, setScatterGroupBy] = useState<'tyre' | 'surface'>(() => {
    return (localStorage.getItem('multiReports_scatterGroupBy') as 'tyre' | 'surface') || 'tyre';
  });
  const [accelScale, setAccelScale] = useState<'Absolute' | 'Proportional'>(() => {
    return (localStorage.getItem('multiReports_accelScale') as 'Absolute' | 'Proportional') || 'Absolute';
  });

  // Export state
  const [isExportPreparing, setIsExportPreparing] = useState(false);
  const [exportFlash, setExportFlash] = useState<'full' | 'details' | null>(null);
  const printContainerRef = useRef<HTMLDivElement>(null);

  // 'details' keeps only Session Conditions and Detailed Run Data, without the per-session Crr plots
  const handleExport = async (scope: 'full' | 'details' = 'full') => {
    // Trigger flash animation
    setExportFlash(scope);
    setTimeout(() => setExportFlash(null), 300);

    setIsExportPreparing(true);

    // Allow React to render the spinner before starting export
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

    // Build title and filename from reference session date
    const referenceSession = referenceSessions?.find((s: any) => s.id === selectedSessionId);
    const refDate = referenceSession?.date
      ? new Date(referenceSession.date).toLocaleDateString()
      : '';
    const titleBase = scope === 'details' ? 'Tyre Report Session Details' : 'Tyre Report';
    const exportTitle = refDate ? `${titleBase} ${refDate}` : titleBase;
    const filenameDate = refDate.replace(/ /g, '_').replace(/[/\\:*?"<>|]/g, '-');
    const filenameBase = titleBase.replace(/ /g, '_');
    const filename = filenameDate ? `${filenameBase}_${filenameDate}.html` : `${filenameBase}.html`;

    // Drop every section ahead of Session Conditions, plus anything marked full-report-only
    const keepSessionDetailsOnly = (clone: HTMLElement) => {
      let previous = clone.querySelector('#section-detailed-data')?.previousElementSibling;
      while (previous) {
        const next = previous.previousElementSibling;
        previous.remove();
        previous = next;
      }
      clone.querySelectorAll('[data-export-scope="full"]').forEach((el) => el.remove());
    };

    await executeExportHtml(printContainerRef, {
      waitTime: 10000,
      title: exportTitle,
      filename,
      onAfterExport: () => setIsExportPreparing(false),
      leafletClipRatio: 0.65,
      leafletWidthPercent: 65,
      // Wide enough for the Detailed Run Data tables to show every column without clipping
      exportWidthPx: 1200,
      prepareClone: scope === 'details' ? keepSessionDetailsOnly : undefined,
    });
  };

  // Export Data: one JSON packet holding every API response the page reads
  const [isExportingData, setIsExportingData] = useState(false);

  const handleExportData = async () => {
    if (!selectedSessionId || isExportingData) return;
    setIsExportingData(true);
    try {
      const packet = await getTyreReportDataExport(selectedSessionId, circumferenceSessionIds);
      const referenceSession = referenceSessions?.find((s: any) => s.id === selectedSessionId);
      const refDate = referenceSession?.date
        ? new Date(referenceSession.date).toLocaleDateString()
        : '';
      const filenameDate = refDate.replace(/ /g, '_').replace(/[/\\:*?"<>|]/g, '-');
      const filename = filenameDate ? `Tyre_Report_Data_${filenameDate}.json` : 'Tyre_Report_Data.json';

      const blob = new Blob([JSON.stringify(packet, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to export tyre report data:', error);
    } finally {
      setIsExportingData(false);
    }
  };

  // Get reference sessions for the dropdown
  const { data: referenceSessions } = useTyreReferenceSessions();
  const { data: sessions, isLoading } = useSessionTyreReference(selectedSessionId);
  const { data: equipmentCatalog } = useEquipmentCatalog();
  const { data: ridersById } = useRidersById();
  // Scope the rolling-radii catalog refresh to this report's session family
  // rather than every config the team owns. Held until `sessions` has loaded so
  // the request isn't first made unscoped — see useWheelCircumferenceData.
  const circumferenceSessionIds = useMemo(
    () => (sessions ?? []).map((session: any) => session?.id).filter(Boolean),
    [sessions],
  );
  const { data: wheelCircumferenceData } = useWheelCircumferenceData(
    circumferenceSessionIds,
    circumferenceSessionIds.length > 0,
  );

  // Save settings to localStorage when they change
  useEffect(() => {
    localStorage.setItem('tyreReports_activeTab', activeTab);
  }, [activeTab]);

  useEffect(() => {
    localStorage.setItem('multiReports_selectedSessionId', selectedSessionId);
  }, [selectedSessionId]);

  useEffect(() => {
    localStorage.setItem('multiReports_pressureUnit', pressureUnit);
  }, [pressureUnit]);

  useEffect(() => {
    localStorage.setItem('multiReports_deltaAverage', deltaAverage);
  }, [deltaAverage]);

  useEffect(() => {
    localStorage.setItem('multiReports_selectedTyreFilter', selectedTyreFilter);
  }, [selectedTyreFilter]);

  useEffect(() => {
    localStorage.setItem('multiReports_chartType', chartType);
  }, [chartType]);

  useEffect(() => {
    localStorage.setItem('multiReports_scatterGroupBy', scatterGroupBy);
  }, [scatterGroupBy]);

  useEffect(() => {
    localStorage.setItem('multiReports_accelScale', accelScale);
  }, [accelScale]);

  // Get all unique tyres used in the sessions
  const getAvailableTyres = () => {
    if (!sessions || sessions.length === 0) {
      return [];
    }

    const tyreSet = new Set<string>();
    sessions.forEach((session: any) => {
      if (session?.configs) {
        session.configs.forEach((config: any) => {
          if (config.tyre_rear_id && !config.exclude_from_plot) {
            const tyreName = getCombinedTyreName(
              config.tyre_front_id,
              config.tyre_rear_id,
              equipmentCatalog
            );
            tyreSet.add(tyreName);
          }
        });
      }
    });

    return Array.from(tyreSet).sort();
  };

  const availableTyres = getAvailableTyres();

  // Collect all unique tyre IDs used across all sessions for wheel circumference plot
  const usedTyreIds = useMemo(() => {
    const tyreIds = new Set<string>();
    if (!sessions || sessions.length === 0) {
      return tyreIds;
    }

    sessions.forEach((session: any) => {
      if (session?.configs) {
        session.configs.forEach((config: any) => {
          if (config.tyre_rear_id && !config.exclude_from_plot) {
            tyreIds.add(config.tyre_rear_id);
          }
          if (config.tyre_front_id && !config.exclude_from_plot) {
            tyreIds.add(config.tyre_front_id);
          }
        });
      }
    });

    return tyreIds;
  }, [sessions]);

  // Create bar plot data for each session
  const createBarPlotData = (session: any) => {
    if (!session?.configs) {
      return { traces: [], subplotData: {} };
    }

    // Determine which field to use based on deltaAverage setting
    const deltaField = deltaAverage === 'Moving' ? 'deltas_rolling' : 'deltas';

    // Process data for bar chart
    const configData: any[] = [];

    session.configs.forEach((config: any) => {
      // Skip configs marked to exclude from plots
      if (config.exclude_from_plot) return;
      if (config.settings_collection?.exclude_tyre_report) return;

      const tyrePressureBar = config.tyre_rear_pressure;
      const tyreId = config.tyre_rear_id;

      if (!tyrePressureBar || !tyreId) return;

      const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);
      const tyreName = getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);

      // Use config-level delta value (same as table)
      const deltaValue = config[deltaField]?.['RollingResistanceNormalised_PG5'];

      if (deltaValue !== null && deltaValue !== undefined) {
        const delta = Array.isArray(deltaValue) ? deltaValue[0] : deltaValue;

        // Scale delta by speed ratio (new_speed / ref_speed)
        const scaledDelta = delta * speedScale;

        configData.push({
          tyreName: tyreName,
          pressure: tyrePressure.toFixed(1),
          delta: scaledDelta,
          config: config.letter,
          letterNumber: config.letter_number,
        });
      }
    });

    // Group by tyre type for separate subplots
    const tyreGroups: { [tyreName: string]: any[] } = {};
    configData.forEach((point) => {
      // Filter by selected tyre if not 'All'
      if (selectedTyreFilter !== 'All' && point.tyreName !== selectedTyreFilter) {
        return;
      }

      if (!tyreGroups[point.tyreName]) {
        tyreGroups[point.tyreName] = [];
      }
      tyreGroups[point.tyreName].push(point);
    });

    // Create data structure for subplots
    const subplotData: { [tyreName: string]: any[] } = {};
    Object.entries(tyreGroups).forEach(([tyreName, points]) => {
      // Sort by pressure
      points.sort((a, b) => parseFloat(a.pressure) - parseFloat(b.pressure));

      subplotData[tyreName] = [
        {
          x: points.map((p) => `${p.pressure} ${pressureUnit}`),
          y: points.map((p) => p.delta),
          type: 'bar',
          name: tyreName,
          hovertemplate:
            '<b>%{fullData.name}</b><br>' + 'Pressure: %{x}<br>' + 'Delta Power: %{y:.3f} W<br>' + '<extra></extra>',
          marker: {
            color: points.map((p) => p.delta),
            colorscale: [
              [0, '#d73027'], // Red for negative values
              [0.5, '#ffffff'], // White for zero
              [1, '#1a9850'], // Green for positive values
            ],
            cmid: 0, // Center colorscale at zero
            line: {
              color: '#333',
              width: 1,
            },
          },
          showlegend: false, // Don't show legend for individual bars
        },
      ];
    });

    return { traces: [], subplotData };
  };

  // Get baseline info for chart subtitle
  const getBaselineInfo = (session: any) => {
    if (!session?.configs) {
      return { tyreName: '', pressure: 0, airspeedKph: 0 };
    }

    const baselineConfig = session.configs.find((config: any) => config.letter_number === 0);
    if (!baselineConfig) {
      return { tyreName: '', pressure: 0, airspeedKph: 0 };
    }

    const tyreName = getCombinedTyreName(
      baselineConfig.tyre_front_id,
      baselineConfig.tyre_rear_id,
      equipmentCatalog
    );
    const pressure = Math.round(convertPressure(baselineConfig.tyre_rear_pressure, pressureUnit));
    const airspeedKph = Math.round(session.airspeed_ref * 3.6 * speedScale);

    return { tyreName, pressure, airspeedKph };
  };

  // Helper: get surface label for a session
  const getSurfaceLabel = (session: any): string => {
    if (
      session.surface_quality !== null
      && session.surface_quality !== undefined
    ) {
      try {
        return SurfaceQuality
          .fromFloat(session.surface_quality).getDescription();
      } catch {
        return `Surface ${session.surface_quality}`;
      }
    }
    return session.location_type || 'Unknown';
  };

  // Helper: extract a numeric value from run data at a given path
  const extractRunValue = (
    run: any, path: string, field: string,
  ): number | null => {
    const raw = run[path]?.[field];
    if (raw === null || raw === undefined) return null;
    return Array.isArray(raw) ? raw[0] : raw;
  };

  // Generic scatter data builder (grouped by tyre)
  // valueExtractor returns a numeric value for each config
  const buildScatterByTyre = (
    valueExtractor: (config: any, session: any) => number | null,
  ) => {
    if (!sessions || sessions.length === 0) {
      return {
        scatterData: {},
        baselineInfo: { tyreName: '', pressure: 0, airspeedKph: 0 },
      };
    }

    const scatterData: { [tyreName: string]: any[] } = {};
    const roundFactor = pressureUnit === 'PSI' ? 10 : 100;

    sessions.forEach((session: any) => {
      if (!session?.configs) return;

      // Sort configs by letter_number to identify primes
      const sorted = [...session.configs].sort(
        (a: any, b: any) =>
          (a.letter_number ?? 999) - (b.letter_number ?? 999),
      );
      // Track first config per (tyre, roundedPressure) in this session
      const seen = new Set<string>();

      sorted.forEach((config: any) => {
        if (!config.runs || config.runs.length === 0) return;
        if (config.exclude_from_plot) return;
        if (config.settings_collection?.exclude_tyre_report) return;

        const tyrePressureBar = config.tyre_rear_pressure;
        const tyreId = config.tyre_rear_id;
        if (!tyrePressureBar || !tyreId) return;

        const tyrePressure = convertPressure(
          tyrePressureBar, pressureUnit,
        );
        const tyreName = getCombinedTyreName(
          config.tyre_front_id, tyreId, equipmentCatalog,
        );

        if (
          selectedTyreFilter !== 'All'
          && tyreName !== selectedTyreFilter
        ) return;

        const value = valueExtractor(config, session);
        if (value === null) return;

        // Determine prime status
        const rounded = Math.round(
          tyrePressure * roundFactor,
        ) / roundFactor;
        const comboKey = `${tyreName}|${rounded}`;
        const isPrime = !seen.has(comboKey);
        if (isPrime) seen.add(comboKey);

        const surfaceLabel = getSurfaceLabel(session);

        if (!scatterData[tyreName]) {
          scatterData[tyreName] = [];
        }

        scatterData[tyreName].push({
          x: tyrePressure,
          y: value,
          sessionName: session.description
            || session.name
            || new Date(session.date).toLocaleDateString(),
          surface: surfaceLabel,
          surfaceValue: session.surface_quality ?? 10.25,
          isPrime,
        });
      });
    });

    const baselineInfo = getBaselineInfo(sessions[0]);
    return { scatterData, baselineInfo };
  };

  // Create scatter plot data for all sessions (grouped by tyre)
  const createScatterPlotData = () => {
    const deltaField = deltaAverage === 'Moving'
      ? 'deltas_rolling' : 'deltas';
    return buildScatterByTyre((config) => {
      const values: number[] = [];
      config.runs.forEach((run: any) => {
        const v = extractRunValue(
          run, deltaField, 'RollingResistanceNormalised_PG5',
        );
        if (v !== null) values.push(v);
      });
      if (values.length === 0) return null;
      const avg = values.reduce((s, v) => s + v, 0)
        / values.length;
      return avg * speedScale;
    });
  };

  // Absolute rolling power at reference speed (by tyre)
  const createAbsoluteRollingPlotData = () => {
    return buildScatterByTyre((config) => {
      const values: number[] = [];
      config.runs.forEach((run: any) => {
        const v = extractRunValue(
          run, 'average', 'RollingResistanceNormalised_PG5',
        );
        if (v !== null) values.push(v);
      });
      if (values.length === 0) return null;
      const avg = values.reduce((s, v) => s + v, 0)
        / values.length;
      return avg * speedScale;
    });
  };

  // Helper: get average road vibration for a config — AccelerationMagStdDev,
  // the standard deviation of the acceleration magnitude |a|. Not
  // AccelerationStdDevMag, which is the root-sum-square of the per-axis SDs.
  const getConfigAccelStdDev = (config: any): number | null => {
    const values: number[] = [];
    config.runs.forEach((run: any) => {
      const v = extractRunValue(
        run, 'average', 'AccelerationMagStdDev_aG3',
      );
      if (v !== null && v !== 0) values.push(v);
    });
    if (values.length === 0) return null;
    return values.reduce((s, v) => s + v, 0) / values.length;
  };

  // Cache baseline accel per session to avoid recomputation
  const baselineAccelCache = useMemo(() => {
    const cache: Record<string, number | null> = {};
    if (!sessions) return cache;
    sessions.forEach((session: any) => {
      if (!session?.configs) return;
      const baseline = session.configs.find(
        (c: any) => c.letter_number === 0,
      );
      if (baseline?.runs?.length > 0) {
        cache[session.id] = getConfigAccelStdDev(baseline);
      }
    });
    return cache;
  }, [sessions]);

  // Whether acceleration data is available in any session
  const hasAccelerationData = useMemo(() => {
    if (!sessions) return false;
    return sessions.some((session: any) =>
      session?.configs?.some((config: any) =>
        config?.runs?.some((run: any) => {
          const v = extractRunValue(
            run, 'average', 'AccelerationMagStdDev_aG3',
          );
          return v !== null && v !== 0;
        }),
      ),
    );
  }, [sessions]);

  // Mean vibration over every config the acceleration charts can
  // plot (all sessions, before the tyre filter). It is the reference for the
  // proportional view, so a config's percentage does not move with the filter.
  const accelMean = useMemo(() => {
    if (!sessions) return null;
    const values: number[] = [];
    sessions.forEach((session: any) => {
      session?.configs?.forEach((config: any) => {
        if (!config.runs || config.runs.length === 0) return;
        if (config.exclude_from_plot) return;
        if (config.settings_collection?.exclude_tyre_report) return;
        if (!config.tyre_rear_pressure || !config.tyre_rear_id) return;
        const v = getConfigAccelStdDev(config);
        if (v !== null) values.push(v);
      });
    });
    if (values.length === 0) return null;
    return values.reduce((s, v) => s + v, 0) / values.length;
  }, [sessions]);

  // Set only while the proportional view is on (and there is a mean to
  // divide by); null means the charts show plain m/s².
  const accelScaleMean = accelScale === 'Proportional' ? accelMean : null;

  // Axis and hover text for the acceleration charts in the selected scale
  const accelLabels = accelScaleMean !== null
    ? {
      absoluteTitle: 'Acceleration StdDev - Proportional',
      absoluteSub: 'Variation from the mean of all configs',
      absoluteAxis: 'Accel StdDev (% from mean)',
      absoluteHover: 'Accel StdDev (% from mean)',
      absoluteFormat: '.1f',
      offsetSub: 'Offset from baseline config, as % of the mean of all configs',
      offsetAxis: 'Accel StdDev Offset (% of mean)',
      offsetFormat: '.1f',
      vsRollingAxis: 'Accel StdDev (% from mean)',
      vsRollingHover: 'Accel StdDev (% from mean)',
      vsRollingLogAxis: 'Accel StdDev % of mean (log)',
      vsRollingLogHover: 'Accel StdDev (% of mean)',
      vsRollingFormat: '.1f',
    }
    : {
      absoluteTitle: 'Acceleration StdDev - Absolute',
      absoluteSub: 'Gate-averaged accelerometer vibration',
      absoluteAxis: 'Accel Mag StdDev (m/s²)',
      absoluteHover: 'Accel StdDev (m/s²)',
      absoluteFormat: '.2f',
      offsetSub: 'Offset from baseline config',
      offsetAxis: 'Accel StdDev Offset (m/s²)',
      offsetFormat: '.3f',
      vsRollingAxis: 'Accel Mag StdDev (m/s²)',
      vsRollingHover: 'Accel StdDev (m/s²)',
      vsRollingLogAxis: 'Accel Mag StdDev m/s² (log)',
      vsRollingLogHover: 'Accel StdDev (m/s²)',
      vsRollingFormat: '.3f',
    };

  // Acceleration StdDev Magnitude — Absolute, or in the proportional view
  // the percentage variation from the mean of all configs (by tyre)
  const createAccelerationPlotData = () => {
    return buildScatterByTyre((config) => {
      const val = getConfigAccelStdDev(config);
      if (val === null || accelScaleMean === null) return val;
      return (val / accelScaleMean - 1) * 100;
    });
  };

  // Acceleration StdDev Magnitude — Offset from baseline (by tyre). In the
  // proportional view the offset is a percentage of the mean of all configs,
  // so it is on the same scale as the proportional chart above.
  const createAccelerationOffsetPlotData = () => {
    return buildScatterByTyre((config, session) => {
      const val = getConfigAccelStdDev(config);
      if (val === null) return null;
      const baseVal = baselineAccelCache[session?.id];
      if (baseVal === null || baseVal === undefined) return null;
      if (accelScaleMean === null) return val - baseVal;
      return (val - baseVal) / accelScaleMean * 100;
    });
  };

  // Accel StdDev (x) vs Rolling Power (y) — by tyre. `forLogAxis` only
  // matters in the proportional view: a log axis cannot show the negative
  // half of a variation, so there x is the percentage OF the mean (mean =
  // 100) instead of the variation FROM it (mean = 0).
  const createAccelVsRollingPlotData = () => {
    if (!sessions || sessions.length === 0) {
      return {
        scatterData: {},
        baselineInfo: { tyreName: '', pressure: 0, airspeedKph: 0 },
      };
    }

    const scatterData: { [tyreName: string]: any[] } = {};

    sessions.forEach((session: any) => {
      if (!session?.configs) return;

      session.configs.forEach((config: any) => {
        if (!config.runs || config.runs.length === 0) return;
        if (config.exclude_from_plot) return;
        if (config.settings_collection?.exclude_tyre_report) {
          return;
        }

        const tyrePressureBar = config.tyre_rear_pressure;
        const tyreId = config.tyre_rear_id;
        if (!tyreId || !tyrePressureBar) return;

        const tyreName = getCombinedTyreName(
          config.tyre_front_id, tyreId, equipmentCatalog,
        );
        if (
          selectedTyreFilter !== 'All'
          && tyreName !== selectedTyreFilter
        ) return;

        // Extract both values per run, average across runs
        const accelVals: number[] = [];
        const rollingVals: number[] = [];
        config.runs.forEach((run: any) => {
          const a = extractRunValue(
            run, 'average', 'AccelerationMagStdDev_aG3',
          );
          const r = extractRunValue(
            run, 'average', 'RollingResistanceNormalised_PG5',
          );
          if (a !== null && a !== 0 && r !== null) {
            accelVals.push(a);
            rollingVals.push(r);
          }
        });

        if (accelVals.length === 0) return;

        const avgAccel = accelVals.reduce((s, v) => s + v, 0)
          / accelVals.length;
        const avgRolling = rollingVals.reduce((s, v) => s + v, 0)
          / rollingVals.length * speedScale;
        const accelX = accelScaleMean !== null
          ? avgAccel / accelScaleMean * 100 - 100
          : avgAccel;

        // Group by pressure (secondary key)
        const tyrePressure = convertPressure(
          tyrePressureBar, pressureUnit,
        );
        const pressureLabel = `${tyrePressure.toFixed(1)}`
          + ` ${pressureUnit.toLowerCase()}`;

        if (!scatterData[tyreName]) {
          scatterData[tyreName] = [];
        }

        scatterData[tyreName].push({
          x: accelX,
          y: avgRolling,
          sessionName: session.description
            || session.name
            || new Date(session.date).toLocaleDateString(),
          surface: pressureLabel,
          surfaceValue: tyrePressure,
        });
      });
    });

    const baselineInfo = getBaselineInfo(sessions[0]);
    return { scatterData, baselineInfo };
  };

  // The log chart cannot show "% from mean" (it crosses zero), so in the
  // proportional view it plots "% of mean": the same points shifted by 100.
  // In m/s² both charts share one dataset.
  const accelLogScatterData = (scatterData: { [tyreName: string]: any[] }) => (
    accelScaleMean !== null
      ? Object.fromEntries(Object.entries(scatterData).map(
        ([tyreName, points]) => [tyreName, points.map((p) => ({ ...p, x: p.x + 100 }))],
      ))
      : scatterData
  );

  // Create scatter plot data grouped by surface
  const createScatterPlotDataBySurface = () => {
    if (!sessions || sessions.length === 0) {
      return {
        scatterData: {},
        baselineInfo: { tyreName: '', pressure: 0, airspeedKph: 0 },
      };
    }

    const deltaField = deltaAverage === 'Moving'
      ? 'deltas_rolling' : 'deltas';
    const scatterData: { [surfaceLabel: string]: any[] } = {};
    const roundFactor = pressureUnit === 'PSI' ? 10 : 100;

    sessions.forEach((session: any) => {
      if (!session?.configs) return;

      const surfaceLabel = getSurfaceLabel(session);

      // Sort configs to identify primes
      const sorted = [...session.configs].sort(
        (a: any, b: any) =>
          (a.letter_number ?? 999) - (b.letter_number ?? 999),
      );
      const seen = new Set<string>();

      sorted.forEach((config: any) => {
        if (!config.runs || config.runs.length === 0) return;
        if (config.exclude_from_plot) return;
        if (config.settings_collection?.exclude_tyre_report) {
          return;
        }

        const tyrePressureBar = config.tyre_rear_pressure;
        const tyreId = config.tyre_rear_id;
        if (!tyrePressureBar || !tyreId) return;

        const tyrePressure = convertPressure(
          tyrePressureBar, pressureUnit,
        );
        const tyreName = getCombinedTyreName(
          config.tyre_front_id, tyreId, equipmentCatalog,
        );

        if (
          selectedTyreFilter !== 'All'
          && tyreName !== selectedTyreFilter
        ) return;

        const configValues: number[] = [];
        config.runs.forEach((run: any) => {
          const v = extractRunValue(
            run, deltaField, 'RollingResistanceNormalised_PG5',
          );
          if (v !== null) configValues.push(v);
        });

        if (configValues.length > 0) {
          const delta = configValues.reduce(
            (sum, val) => sum + val, 0,
          ) / configValues.length;
          const scaledDelta = delta * speedScale;

          // Prime detection
          const rounded = Math.round(
            tyrePressure * roundFactor,
          ) / roundFactor;
          const comboKey = `${tyreName}|${rounded}`;
          const isPrime = !seen.has(comboKey);
          if (isPrime) seen.add(comboKey);

          if (!scatterData[surfaceLabel]) {
            scatterData[surfaceLabel] = [];
          }

          scatterData[surfaceLabel].push({
            x: tyrePressure,
            y: scaledDelta,
            sessionName: session.description
              || session.name
              || new Date(session.date).toLocaleDateString(),
            tyreName,
            surfaceValue: session.surface_quality ?? 10.25,
            isPrime,
          });
        }
      });
    });

    const baselineInfo = getBaselineInfo(sessions[0]);
    return { scatterData, baselineInfo };
  };

  // Shared x/y axis range for the "All Sessions Combined" delta rolling
  // resistance offset plots (by tyre and by surface), so both charts use
  // the same scale instead of each autoscaling to its own data.
  const getCombinedOffsetRange = (scatterDataSets: any[]) => {
    const xValues: number[] = [];
    const yValues: number[] = [];
    scatterDataSets.forEach((data) => {
      Object.values(data).forEach((points: any) => {
        points.forEach((p: any) => {
          if (p.x != null && isFinite(p.x)) xValues.push(p.x);
          if (p.y != null && isFinite(p.y)) yValues.push(p.y);
        });
      });
    });
    return {
      xRange: computeAxisRange(xValues),
      yRange: computeAxisRange(yValues, { showNegativeMargin: true }),
    };
  };

  const handleSessionSelect = (sessionId: string) => {
    setSelectedSessionId(sessionId);
    // Reset tyre filter when changing sessions
    setSelectedTyreFilter('All');
  };

  return (
    <div className="cr-w-full cr-space-y-8 cr-p-6">
      <div className="cr-mb-8">
        <SectionHeader title="Tyre Reports" />

        {/* Reference Session Selector */}
        <div className="cr-mb-6 screen-only">
          <label className="cr-block cr-text-base cr-font-medium cr-text-blue-900 cr-mb-2">
            Select Reference Session
          </label>
          <select
            value={selectedSessionId}
            onChange={(e) => handleSessionSelect(e.target.value)}
            className="cr-w-full cr-p-3 cr-text-base cr-text-blue-900 cr-border cr-border-gray-300 cr-rounded-lg focus:cr-ring-2 focus:cr-ring-blue-500 focus:cr-border-blue-500"
          >
            <option value="">Select a reference session...</option>
            {referenceSessions?.map((session: any) => (
              <option key={session.id} value={session.id}>
                {session.date || new Date(session.created_at).toLocaleDateString()} - {session.description || session.name || 'Unnamed Session'}
              </option>
            ))}
          </select>
        </div>

        {/* Tyre Filter */}
        {availableTyres.length > 0 && (
          <div className="cr-mb-6 screen-only">
            <label className="cr-block cr-text-base cr-font-medium cr-text-blue-900 cr-mb-2">
              Filter by Tyre
            </label>
            <select
              value={selectedTyreFilter}
              onChange={(e) => setSelectedTyreFilter(e.target.value)}
              className="cr-w-full cr-p-3 cr-text-base cr-text-blue-900 cr-border cr-border-gray-300 cr-rounded-lg focus:cr-ring-2 focus:cr-ring-blue-500 focus:cr-border-blue-500"
            >
              <option value="All">All Tyres</option>
              {availableTyres.map((tyreName) => (
                <option key={tyreName} value={tyreName}>
                  {tyreName}
                </option>
              ))}
            </select>
          </div>
        )}

        {/* Settings Controls - Only show in Performance tab */}
        {activeTab === 'performance' && (
          <>
            {/* Settings Controls */}
            <div className="cr-mb-6 cr-flex cr-items-center cr-justify-start cr-space-x-8 cr-flex-wrap cr-gap-y-4">
          {/* Pressure Unit Selector */}
          <div className="cr-flex cr-items-center cr-space-x-4">
            <span className="cr-text-base cr-font-medium cr-text-blue-900">Pressure Unit:</span>
            <div className="cr-flex cr-space-x-3">
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="pressureUnit"
                  value="Bar"
                  checked={pressureUnit === 'Bar'}
                  onChange={(e) => setPressureUnit(e.target.value as 'Bar' | 'PSI')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Bar</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="pressureUnit"
                  value="PSI"
                  checked={pressureUnit === 'PSI'}
                  onChange={(e) => setPressureUnit(e.target.value as 'Bar' | 'PSI')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">PSI</span>
              </label>
            </div>
          </div>

          {/* Delta Average Selector */}
          <div className="cr-flex cr-items-center cr-space-x-4">
            <span className="cr-text-base cr-font-medium cr-text-blue-900">Delta Average:</span>
            <div className="cr-flex cr-space-x-3">
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="deltaAverage"
                  value="Constant"
                  checked={deltaAverage === 'Constant'}
                  onChange={(e) => setDeltaAverage(e.target.value as 'Constant' | 'Moving')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Constant Average</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="deltaAverage"
                  value="Moving"
                  checked={deltaAverage === 'Moving'}
                  onChange={(e) => setDeltaAverage(e.target.value as 'Constant' | 'Moving')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Nearest Pair</span>
              </label>
            </div>
          </div>

          {/* Chart Type Selector */}
          <div className="cr-flex cr-items-center cr-space-x-4">
            <span className="cr-text-base cr-font-medium cr-text-blue-900">Chart Type:</span>
            <div className="cr-flex cr-space-x-3">
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="chartType"
                  value="bar"
                  checked={chartType === 'bar'}
                  onChange={(e) => setChartType(e.target.value as 'bar' | 'scatter')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Bar Chart</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="chartType"
                  value="scatter"
                  checked={chartType === 'scatter'}
                  onChange={(e) => setChartType(e.target.value as 'bar' | 'scatter')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Scatter Plot</span>
              </label>
            </div>
          </div>

          {/* Acceleration Scale Selector */}
          {hasAccelerationData && (
            <div className="cr-flex cr-items-center cr-space-x-4">
              <span className="cr-text-base cr-font-medium cr-text-blue-900">Acceleration:</span>
              <div className="cr-flex cr-space-x-3">
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="accelScale"
                    value="Absolute"
                    checked={accelScale === 'Absolute'}
                    onChange={(e) => setAccelScale(e.target.value as 'Absolute' | 'Proportional')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-base cr-text-blue-900">Absolute (m/s²)</span>
                </label>
                <label className="cr-flex cr-cursor-pointer cr-items-center">
                  <input
                    type="radio"
                    name="accelScale"
                    value="Proportional"
                    checked={accelScale === 'Proportional'}
                    onChange={(e) => setAccelScale(e.target.value as 'Absolute' | 'Proportional')}
                    className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                  />
                  <span className="cr-text-base cr-text-blue-900">Proportional (% of mean)</span>
                </label>
              </div>
            </div>
          )}
        </div>

        {/* Scatter Plot Grouping Options - Only show when scatter plot is selected */}
        {chartType === 'scatter' && (
          <div className="cr-mb-6 cr-flex cr-items-center cr-space-x-4">
            <span className="cr-text-base cr-font-medium cr-text-blue-900">Group By:</span>
            <div className="cr-flex cr-space-x-3">
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="scatterGroupBy"
                  value="tyre"
                  checked={scatterGroupBy === 'tyre'}
                  onChange={(e) => setScatterGroupBy(e.target.value as 'tyre' | 'surface')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Separate Tyres</span>
              </label>
              <label className="cr-flex cr-cursor-pointer cr-items-center">
                <input
                  type="radio"
                  name="scatterGroupBy"
                  value="surface"
                  checked={scatterGroupBy === 'surface'}
                  onChange={(e) => setScatterGroupBy(e.target.value as 'tyre' | 'surface')}
                  className="cr-mr-2 cr-text-blue-600 focus:cr-ring-blue-500"
                />
                <span className="cr-text-base cr-text-blue-900">Separate Surfaces</span>
              </label>
            </div>
          </div>
        )}

        {/* Speed Scaling Input */}
        {selectedSessionId && sessions && sessions.length > 0 && sessions[0]?.airspeed_ref && (
          <div className="cr-mb-6 cr-p-4 cr-bg-gray-50 cr-rounded-lg">
            <div className="cr-flex cr-items-center cr-justify-between cr-mb-3">
              <label className="cr-text-base cr-font-medium cr-text-blue-900">
                Speed Scaling
              </label>
              <button
                onClick={() => setSpeedScale(1)}
                className="cr-text-base cr-text-blue-600 hover:cr-text-blue-800 cr-underline"
              >
                Reset to reference ({(sessions[0].airspeed_ref * 3.6).toFixed(1)} km/h)
              </button>
            </div>
            <div className="cr-flex cr-items-center cr-space-x-4">
              <input
                type="number"
                min="1"
                step="1"
                value={Math.round(sessions[0].airspeed_ref * 3.6 * speedScale)}
                onChange={(e) => {
                  const newSpeedKph = parseFloat(e.target.value);
                  const refSpeedKph = sessions[0].airspeed_ref * 3.6;
                  setSpeedScale(newSpeedKph / refSpeedKph);
                }}
                className="cr-w-24 cr-p-2 cr-text-base cr-text-blue-900 cr-border cr-border-gray-300 cr-rounded-lg focus:cr-ring-2 focus:cr-ring-blue-500 focus:cr-border-blue-500"
              />
              <span className="cr-text-base cr-text-blue-900">km/h</span>
            </div>
            <div className="cr-mt-2 cr-text-base cr-text-gray-500">
              Scales the power delta values based on speed ratio (new_speed / ref_speed)
            </div>
          </div>
        )}
          </>
        )}
      </div>

      {/* Tab Navigation */}
      <div className="cr-border-b cr-border-gray-200 cr-mb-6">
        <nav className="cr-flex cr-space-x-8" aria-label="Tabs">
          <button
            onClick={() => setActiveTab('coordination')}
            className={`cr-py-4 cr-px-1 cr-border-b-2 cr-font-medium cr-text-base cr-transition-colors ${
              activeTab === 'coordination'
                ? 'cr-border-blue-500 cr-text-blue-600'
                : 'cr-border-transparent cr-text-blue-900 hover:cr-text-blue-600 hover:cr-border-gray-300'
            }`}
          >
            Coordination
          </button>
          <button
            onClick={() => setActiveTab('performance')}
            className={`cr-py-4 cr-px-1 cr-border-b-2 cr-font-medium cr-text-base cr-transition-colors ${
              activeTab === 'performance'
                ? 'cr-border-blue-500 cr-text-blue-600'
                : 'cr-border-transparent cr-text-blue-900 hover:cr-text-blue-500 hover:cr-border-gray-300'
            }`}
          >
            Performance
          </button>
          <button
            onClick={() => setActiveTab('temperature')}
            className={`cr-py-4 cr-px-1 cr-border-b-2 cr-font-medium cr-text-base cr-transition-colors ${
              activeTab === 'temperature'
                ? 'cr-border-blue-500 cr-text-blue-600'
                : 'cr-border-transparent cr-text-blue-900 hover:cr-text-blue-500 hover:cr-border-gray-300'
            }`}
          >
            Temperature
          </button>
          <button
            onClick={() => setActiveTab('report')}
            className={`cr-py-4 cr-px-1 cr-border-b-2 cr-font-medium cr-text-base cr-transition-colors ${
              activeTab === 'report'
                ? 'cr-border-blue-500 cr-text-blue-600'
                : 'cr-border-transparent cr-text-blue-900 hover:cr-text-blue-500 hover:cr-border-gray-300'
            }`}
          >
            Report
          </button>
        </nav>
      </div>

      {/* Coordination Tab Content */}
      {activeTab === 'coordination' && (
        <CoordinationTab
          sessions={sessions}
          selectedSessionId={selectedSessionId}
          equipmentCatalog={equipmentCatalog}
          ridersById={ridersById}
        />
      )}

      {/* Performance Tab Content */}
      {activeTab === 'performance' && (
        <>
          {/* Loading State */}
          {isLoading && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {/* Session Plots */}
          {!isLoading && sessions && sessions.length > 0 && (
            <div className="cr-space-y-12">
              {chartType === 'bar' ? (
                // Bar Chart View - show each session separately
                sessions.map((session, index) => {
                  const barPlotData = createBarPlotData(session);
                  const baselineInfo = getBaselineInfo(session);

                  return (
                    <div key={session.id || index} className="cr-space-y-4">
                      <h2 className="cr-text-xl cr-font-semibold cr-text-blue-900">
                        {session.description || 'Session'} - {session?.date ? new Date(session.date).toLocaleDateString() : 'No date'}
                      </h2>
                      {Object.keys(barPlotData.subplotData).length > 0 ? (
                        <TyreDeltaBarChartApex
                          sessionDate={session?.date}
                          sessionDescription={session?.description}
                          airspeedKph={baselineInfo.airspeedKph}
                          riderMassKg={session?.rider_mass}
                          airTemperatureC={session?.air_temperature}
                          baselineTyreName={baselineInfo.tyreName}
                          baselinePressure={baselineInfo.pressure}
                          subplotData={barPlotData.subplotData}
                          pressureUnit={pressureUnit}
                          deltaAverage={deltaAverage}
                          equipmentCatalog={equipmentCatalog}
                          height={600}
                        />
                      ) : (
                        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                          <p>No tyre data available for this session.</p>
                        </div>
                      )}
                    </div>
                  );
                })
              ) : (
                // Scatter Plot View - show all sessions combined
                (() => {
                  const deltaResult = scatterGroupBy === 'tyre'
                    ? createScatterPlotData()
                    : createScatterPlotDataBySurface();
                  const { baselineInfo } = deltaResult;
                  // One dataset is enough for the range: the two groupings emit
                  // the same points from the same runs and differ only in the
                  // key they are filed under, so their combined extent is
                  // either one's extent. Building the other grouping here would
                  // be a second full pass over every session/config/run, on
                  // every render, for numbers we already have.
                  const combinedOffsetRange = getCombinedOffsetRange(
                    [deltaResult.scatterData],
                  );
                  const rollingResult =
                    createAbsoluteRollingPlotData();
                  const accelResult =
                    createAccelerationPlotData();
                  const accelOffsetResult =
                    createAccelerationOffsetPlotData();
                  const groupLabel = scatterGroupBy === 'tyre'
                    ? '(by Tyre)' : '(by Surface)';
                  const kph = baselineInfo.airspeedKph || 0;
                  const noData = (
                    <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                      <p>No tyre data available for scatter plot.</p>
                    </div>
                  );

                  return (
                    <div className="cr-space-y-6">
                      <h2 className="cr-text-xl cr-font-semibold cr-text-blue-900">
                        All Sessions Combined {groupLabel}
                      </h2>

                      {/* 1: Delta Rolling Resistance */}
                      {Object.keys(deltaResult.scatterData).length > 0 ? (
                        <TyreDeltaScatterChart
                          airspeedKph={kph}
                          baselineTyreName={baselineInfo.tyreName}
                          baselinePressure={baselineInfo.pressure}
                          scatterData={deltaResult.scatterData}
                          pressureUnit={pressureUnit}
                          deltaAverage={deltaAverage}
                          equipmentCatalog={equipmentCatalog}
                          height={600}
                          groupBy={scatterGroupBy}
                          lineFromPrimeOnly={true}
                          xRangeOverride={combinedOffsetRange.xRange}
                          yRangeOverride={combinedOffsetRange.yRange}
                        />
                      ) : noData}

                      {/* 2: Rolling Power at Speed */}
                      {Object.keys(rollingResult.scatterData).length > 0 ? (
                        <TyreDeltaScatterChart
                          airspeedKph={kph}
                          baselineTyreName={baselineInfo.tyreName}
                          baselinePressure={baselineInfo.pressure}
                          scatterData={rollingResult.scatterData}
                          pressureUnit={pressureUnit}
                          deltaAverage={deltaAverage}
                          equipmentCatalog={equipmentCatalog}
                          height={600}
                          groupBy={scatterGroupBy}
                          lineFromPrimeOnly={true}
                          yAxisLabel="Rolling Power<br>(Watts)"
                          plotTitleMain={
                            'All Sessions (by Tyre) - Absolute'
                            + ` at ${kph} kph`
                          }
                          plotTitleSub={
                            'Line through initial config only'
                          }
                          hoverYLabel="Rolling Power"
                          hoverYFormat=".1f"
                        />
                      ) : noData}

                      {/* 3a: Acceleration StdDev - Offset */}
                      {hasAccelerationData && Object.keys(accelOffsetResult.scatterData).length > 0 && (
                        <TyreDeltaScatterChart
                          airspeedKph={kph}
                          baselineTyreName={baselineInfo.tyreName}
                          baselinePressure={baselineInfo.pressure}
                          scatterData={accelOffsetResult.scatterData}
                          pressureUnit={pressureUnit}
                          deltaAverage={deltaAverage}
                          equipmentCatalog={equipmentCatalog}
                          height={600}
                          groupBy={scatterGroupBy}
                          lineFromPrimeOnly={true}
                          yAxisLabel={accelLabels.offsetAxis}
                          plotTitleMain={
                            'Acceleration StdDev - Offset'
                          }
                          plotTitleSub={accelLabels.offsetSub}
                          hoverYLabel={accelLabels.offsetAxis}
                          hoverYFormat={accelLabels.offsetFormat}
                        />
                      )}

                      {/* 3b: Acceleration StdDev - Absolute */}
                      {hasAccelerationData && Object.keys(accelResult.scatterData).length > 0 && (
                        <TyreDeltaScatterChart
                          airspeedKph={kph}
                          baselineTyreName={baselineInfo.tyreName}
                          baselinePressure={baselineInfo.pressure}
                          scatterData={accelResult.scatterData}
                          pressureUnit={pressureUnit}
                          deltaAverage={deltaAverage}
                          equipmentCatalog={equipmentCatalog}
                          height={600}
                          groupBy={scatterGroupBy}
                          lineFromPrimeOnly={true}
                          yAxisLabel={accelLabels.absoluteAxis}
                          plotTitleMain={accelLabels.absoluteTitle}
                          plotTitleSub={accelLabels.absoluteSub}
                          hoverYLabel={accelLabels.absoluteHover}
                          hoverYFormat={accelLabels.absoluteFormat}
                        />
                      )}

                      {/* 4: Rolling Power vs Accel StdDev */}
                      {hasAccelerationData && (() => {
                        const avr = createAccelVsRollingPlotData();
                        if (Object.keys(avr.scatterData).length === 0) {
                          return null;
                        }
                        const commonProps = {
                          airspeedKph: kph,
                          baselineTyreName: baselineInfo.tyreName,
                          baselinePressure: baselineInfo.pressure,
                          scatterData: avr.scatterData,
                          pressureUnit,
                          deltaAverage,
                          equipmentCatalog,
                          height: 600,
                          groupBy: scatterGroupBy as 'tyre' | 'surface',
                          showLines: false,
                          yAxisLabel: 'Rolling Power<br>(Watts)',
                          hoverYLabel: 'Rolling Power',
                          hoverYFormat: '.1f',
                          hoverXLabel: accelLabels.vsRollingHover,
                          hoverXFormat: accelLabels.vsRollingFormat,
                        };
                        const logScatterData = accelLogScatterData(avr.scatterData);
                        return (
                          <>
                            <TyreDeltaScatterChart
                              {...commonProps}
                              xAxisLabel={accelLabels.vsRollingAxis}
                              plotTitleMain={
                                'Rolling Power vs Accel StdDev'
                                + ' - Linear Scale'
                              }
                              plotTitleSub={
                                `Normalised at ${kph} kph`
                              }
                            />
                            <TyreDeltaScatterChart
                              {...commonProps}
                              scatterData={logScatterData}
                              hoverXLabel={accelLabels.vsRollingLogHover}
                              xAxisLabel={accelLabels.vsRollingLogAxis}
                              xAxisType="log"
                              plotTitleMain={
                                'Rolling Power vs Accel StdDev'
                                + ' - Log Scale'
                              }
                              plotTitleSub={
                                `Normalised at ${kph} kph`
                              }
                            />
                          </>
                        );
                      })()}
                    </div>
                  );
                })()
              )}
            </div>
          )}

          {/* No Data State */}
          {!isLoading && selectedSessionId && (!sessions || sessions.length === 0) && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
              <p>No session data found for the selected reference session.</p>
            </div>
          )}

          {/* No Selection State */}
          {!selectedSessionId && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
              <p>Please select a reference session to view tyre reports.</p>
            </div>
          )}
        </>
      )}

      {/* Temperature Tab Content */}
      {activeTab === 'temperature' && (
        <>
          {/* Loading State */}
          {isLoading && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {/* Temperature Plots for Each Session */}
          {!isLoading && sessions && sessions.length > 0 && (
            <div className="cr-space-y-8">
              {sessions.map((session, index) => {
                // Get the selected meteorological source from weather_controller_dict
                const selectionMap: Record<string, string> = {
                  weather_api: 'Weather Api',
                  probe: 'Probe',
                  probe_hybrid: 'Probe-API',
                  local_station: 'Local Weather Station',
                  manual: 'Manual',
                  bike_computer: 'Bike Computer',
                };
                const selectedSource = session?.weather_controller_dict?.selection
                  ? selectionMap[session.weather_controller_dict.selection] || 'Weather Api'
                  : 'Weather Api';

                return (
                  <div key={session.id || index} className="cr-rounded-lg cr-border cr-border-gray-200 cr-bg-white cr-p-6 cr-shadow-sm">
                    <h2 className="cr-mb-4 cr-text-xl cr-font-semibold cr-text-blue-900">
                      {session.description || 'Session'} - {session?.date ? new Date(session.date).toLocaleDateString() : 'No date'}
                    </h2>

                    {/* Combined plots (first session only) */}
                    {index === 0 && (
                      <div className="cr-grid cr-grid-cols-1 md:cr-grid-cols-2 cr-gap-6 cr-mb-6">
                        <div>
                          <h3 className="cr-mb-3 cr-text-lg cr-font-medium cr-text-blue-900">Combined Crr / Temp Trends</h3>
                          <CrrTempCombinedPlot
                            combined={session?.repeat_parameters?.CrrTempDxDy}
                            slope={session?.repeat_parameters?.CrrTempDxDy}
                          />
                        </div>
                        <div>
                          <h3 className="cr-mb-3 cr-text-lg cr-font-medium cr-text-blue-900">Combined Crr % Change</h3>
                          <CrrTempCombinedPlot
                            combined={session?.repeat_parameters?.CrrTempDxDyPct}
                            slope={session?.repeat_parameters?.CrrTempDxDyPct}
                            isPercentage
                          />
                        </div>
                      </div>
                    )}

                    {/* Three-column layout on wider screens */}
                    <div className="cr-grid cr-grid-cols-1 lg:cr-grid-cols-2 xl:cr-grid-cols-3 cr-gap-6">
                      {/* Left Column: Temperature Plot */}
                      <div className="cr-flex cr-flex-col">
                        <h3 className="cr-mb-3 cr-text-lg cr-font-medium cr-text-blue-900">Temperature</h3>
                        {session?.weather_controller_dict ? (
                          <WeatherTemperaturePlot
                            weatherControllerDict={session.weather_controller_dict}
                            selectedSource={selectedSource}
                            utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
                          />
                        ) : (
                          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                            <p>No weather data available for this session.</p>
                          </div>
                        )}
                      </div>

                      {/* Right Column: Crr Repeats Plot */}
                      <div className="cr-flex cr-flex-col">
                        <h3 className="cr-mb-3 cr-text-lg cr-font-medium cr-text-blue-900">Crr Repeats</h3>
                        {session?.repeat_parameters?.Full ? (
                          <CrrTempFullPlot
                            data={session.repeat_parameters.Full}
                            title="Crr Repeats"
                          />
                        ) : (
                          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                            <p>No repeat data available for this session.</p>
                          </div>
                        )}
                      </div>

                      {/* Third Column: Repeat Parameters */}
                      <div className="cr-flex cr-flex-col">
                        <h3 className="cr-mb-3 cr-text-lg cr-font-medium cr-text-blue-900">Crr Pct</h3>
                        {session?.repeat_parameters?.FullPct ? (
                          <CrrTempFullPlot
                            data={session.repeat_parameters.FullPct}
                            title="Crr Pct"
                            isPercentage
                          />
                        ) : (
                          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                            <p>No Crr percentage data available for this session.</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* No Data State */}
          {!isLoading && selectedSessionId && (!sessions || sessions.length === 0) && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
              <p>No session data found for the selected reference session.</p>
            </div>
          )}

          {/* No Selection State */}
          {!selectedSessionId && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
              <p>Please select a reference session to view temperature data.</p>
            </div>
          )}
        </>
      )}

      {/* Report Tab Content */}
      {activeTab === 'report' && (
        <div ref={printContainerRef} className="overflow-auto">
          {/* Print logo - appears on every printed page */}
          <div className="print-logo">
            <img src={streamlinesLogo} alt="Streamlines" />
          </div>

          {/* Export Preparing Spinner */}
          {isExportPreparing && createPortal(
            <div
              className="screen-only"
              style={{
                position: 'fixed',
                inset: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 99999,
              }}
            >
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  border: '4px solid rgba(255, 255, 255, 0.3)',
                  borderTop: '4px solid #22c55e',
                  borderRadius: '50%',
                  animation: 'spin 1s linear infinite',
                }}
              />
              <p style={{ color: 'white', marginTop: '16px', fontSize: '18px', fontWeight: 500 }}>
                Exporting HTML...
              </p>
              <style>{`
                @keyframes spin {
                  0% { transform: rotate(0deg); }
                  100% { transform: rotate(360deg); }
                }
              `}</style>
            </div>,
            document.body
          )}


          {/* Loading State */}
          {isLoading && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center">
              <Spinner isFetching={true} />
            </div>
          )}

          {/* Report Table */}
          {!isLoading && sessions && sessions.length > 0 && (
            <div className="cr-space-y-8">
              {/* Combined Tyre Report Header with Export HTML and Export Data Buttons */}
              <div className="cr-flex cr-items-start cr-justify-between">
                <h2 className="font-montserrat cr-text-2xl cr-font-bold cr-text-blue-900 screen-only">Combined Tyre Report</h2>
                <div className="cr-flex cr-flex-col cr-items-stretch cr-gap-2 screen-only">
                  {([
                    ['full', 'Export HTML (Full)', 'Export the whole report'],
                    ['details', 'Export HTML (Session Details)',
                      'Export only Session Conditions and Detailed Run Data, without the Crr plots'],
                  ] as const).map(([mode, label, title]) => (
                    <button
                      key={mode}
                      onClick={() => handleExport(mode)}
                      title={title}
                      className={`cr-flex cr-items-center cr-gap-2 cr-px-4 cr-py-2 cr-font-medium
                        cr-rounded-lg cr-transition-all cr-duration-200 focus:cr-outline-none
                        focus:cr-ring-2 focus:cr-ring-offset-2 ${
                        exportFlash === mode
                          ? 'cr-bg-green-400 cr-scale-105 cr-shadow-lg'
                          : 'cr-bg-green-600 hover:cr-bg-green-700 cr-text-white focus:cr-ring-green-500'
                      }`}
                      style={exportFlash === mode ? { boxShadow: '0 0 20px rgba(34, 197, 94, 0.6)' } : undefined}
                    >
                      <HiOutlineDocumentArrowDown className="cr-size-5" />
                      <span>{label}</span>
                    </button>
                  ))}
                  <button
                    onClick={handleExportData}
                    disabled={isExportingData}
                    title="Download the API responses behind this report as one JSON file"
                    className="cr-flex cr-items-center cr-gap-2 cr-px-4 cr-py-2 cr-font-medium cr-rounded-lg
                      cr-transition-all cr-duration-200 focus:cr-outline-none focus:cr-ring-2
                      focus:cr-ring-offset-2 cr-bg-blue-600 hover:cr-bg-blue-700 cr-text-white
                      focus:cr-ring-blue-500 disabled:cr-opacity-60 disabled:cr-cursor-wait"
                  >
                    <HiOutlineArrowDownTray className="cr-size-5" />
                    <span>{isExportingData ? 'Exporting Data...' : 'Export Data'}</span>
                  </button>
                </div>
              </div>

              {/* Contents */}
              <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 cr-mb-6">
                <h4 className="cr-text-md cr-font-semibold cr-text-blue-900 cr-mb-2">Contents</h4>
                <ul className="cr-list-none cr-space-y-1 cr-text-sm cr-pl-6">
                  {sessions[0]?.summary && sessions[0].summary.length > 0 && (
                    <li>
                      <a href="#section-summary" className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline">
                        Summary
                      </a>
                    </li>
                  )}
                  <li>
                    <a href="#section-combined-results" className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline">
                      Combined Results
                    </a>
                  </li>
                  <li>
                    <a href="#section-wheel-circumference" className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline">
                      Wheel Circumference
                    </a>
                  </li>
                  <li>
                    <a href="#section-temperature-compensation" className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline">
                      Tyre Temperature Compensation
                    </a>
                  </li>
                  <li>
                    <a href="#section-individual-sessions" className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline">
                      Individual Session Summarised Results
                    </a>
                  </li>
                  <li>
                    <a href="#section-detailed-data" className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline">
                      Session Conditions and Detailed Run Data
                    </a>
                  </li>
                </ul>
              </div>

              {/* Summary from reference session */}
              {sessions[0]?.summary && sessions[0].summary.length > 0 && (
                <div id="section-summary" className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 cr-mb-6">
                  <h4 className="cr-text-md cr-font-semibold cr-text-blue-900 cr-mb-2">Summary</h4>
                  <div
                    className="cr-text-sm cr-text-blue-900"
                    dangerouslySetInnerHTML={{ __html: sessions[0].summary }}
                  />
                </div>
              )}

              {/* Test Methodology */}
              <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 cr-mb-6">
                <h4 className="cr-text-md cr-font-semibold cr-text-blue-900 cr-mb-2">Test Methodology</h4>
                <div className="cr-text-sm cr-text-blue-900 cr-space-y-2">
                  <p>
                    All Crr values are derived from the energy remainder when gravitational, kinetic, rider,
                      and aerodynamic energy is removed.
                  </p>
                  <p>
                    The Aerodynamic energy is calculated from a reference CdA, which is measured
                    using an estimated Crr from known values on a smooth road.
                  </p>
                  <p>
                    For further information on the fixed CdA / Crr methodology and why the
                    circular nature still allows precise differences to be measured - see{' '}
                    <a
                      href="https://images.streamlinesaero.com/Measurement%20of%20CdA%20and%20Crr%20using%20fixed%20estimates.pdf"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="cr-text-blue-600 hover:cr-text-blue-800 hover:cr-underline"
                    >
                      link
                    </a>
                  </p>
                  {deltaAverage === 'Moving' && (
                    <p>
                      All headline values are listed in Wattage differences to the baseline
                      configuration on the same road surface. Wherever possible, these values use
                      a &apos;nearest-pair&apos; average, where the nearest baselines before and
                      after the comparison are used as the reference.
                    </p>
                  )}
                  <p>
                    Within the details of the report measured Crr values will be listed, these values are derived from
                    the energy remainder. Technically, these values are not truly Crr values, since tyre size changes
                    or tyre texture may change the aerodynamic drag. For this reason all test airspeeds targeted
                    {sessions[0]?.airspeed_ref ? ` ${Math.round(sessions[0].airspeed_ref * 3.6)} km/h` : ''},
                      however in practice, there is variation in airspeed.
                    </p>
                    <p>For tyre testing, this speed normalisation, uses a constant force
                    representation, which may result
                    in minor performance errors of magnitude, but not performance direction, and these errors will be
                    negligible if test speeds are kept within a narrow range.
                  </p>
                </div>
              </div>

              <h3 id="section-combined-results" className="cr-text-lg cr-font-semibold cr-text-blue-900 screen-only">Combined Results</h3>
              <div className="print-only print-section-header">Combined Results</div>

              {/* Scatter Plots Section */}
              <div className="cr-flex cr-flex-col cr-gap-6 print-scale-80">
                {/* 1 & 2. All Sessions Combined (by Surface) / (by Tyre) - Offset */}
                {/* Both share one x/y scale so they can be compared directly. */}
                {(() => {
                  const { scatterData: surfaceScatterData, baselineInfo: surfaceBaselineInfo } =
                    createScatterPlotDataBySurface();
                  const { scatterData: tyreScatterData, baselineInfo: tyreBaselineInfo } =
                    createScatterPlotData();
                  const combinedOffsetRange = getCombinedOffsetRange(
                    [tyreScatterData, surfaceScatterData],
                  );

                  return (
                    <>
                      <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break">
                        <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                          All Sessions Combined (by Surface)
                        </h3>
                        {Object.keys(surfaceScatterData).length > 0 ? (
                          <TyreDeltaScatterChart
                            airspeedKph={surfaceBaselineInfo.airspeedKph}
                            baselineTyreName={surfaceBaselineInfo.tyreName}
                            baselinePressure={surfaceBaselineInfo.pressure}
                            scatterData={surfaceScatterData}
                            pressureUnit={pressureUnit}
                            deltaAverage={deltaAverage}
                            equipmentCatalog={equipmentCatalog}
                            height={600}
                            groupBy="surface"
                            showLogo={false}
                            lineFromPrimeOnly={true}
                            xRangeOverride={combinedOffsetRange.xRange}
                            yRangeOverride={combinedOffsetRange.yRange}
                          />
                        ) : (
                          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                            <p>No data available for chart.</p>
                          </div>
                        )}
                      </div>

                      <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break print-page-break-before">
                        <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                          All Sessions Combined (by Tyre) - Offset
                        </h3>
                        {Object.keys(tyreScatterData).length > 0 ? (
                          <TyreDeltaScatterChart
                            airspeedKph={tyreBaselineInfo.airspeedKph}
                            baselineTyreName={tyreBaselineInfo.tyreName}
                            baselinePressure={tyreBaselineInfo.pressure}
                            scatterData={tyreScatterData}
                            pressureUnit={pressureUnit}
                            deltaAverage={deltaAverage}
                            equipmentCatalog={equipmentCatalog}
                            height={600}
                            groupBy="tyre"
                            showLogo={false}
                            lineFromPrimeOnly={true}
                            xRangeOverride={combinedOffsetRange.xRange}
                            yRangeOverride={combinedOffsetRange.yRange}
                          />
                        ) : (
                          <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                            <p>No data available for chart.</p>
                          </div>
                        )}
                      </div>
                    </>
                  );
                })()}

                {/* 3. All Sessions Combined (by Tyre) - Absolute */}
                {(() => {
                  const { scatterData, baselineInfo } =
                    createAbsoluteRollingPlotData();
                  const kph = baselineInfo.airspeedKph || 0;
                  return (
                    <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break">
                      <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                        All Sessions Combined (by Tyre) - Absolute
                      </h3>
                      {Object.keys(scatterData).length > 0 ? (
                        <TyreDeltaScatterChart
                          airspeedKph={kph}
                          baselineTyreName={baselineInfo.tyreName}
                          baselinePressure={baselineInfo.pressure}
                          scatterData={scatterData}
                          pressureUnit={pressureUnit}
                          deltaAverage={deltaAverage}
                          equipmentCatalog={equipmentCatalog}
                          height={600}
                          groupBy="tyre"
                          showLogo={false}
                          lineFromPrimeOnly={true}
                          yAxisLabel="Rolling Power<br>(Watts)"
                          plotTitleMain={
                            'Rolling Power by Tyre and Pressure'
                            + ` at ${kph} kph`
                          }
                          plotTitleSub={
                            'Line through initial config only'
                          }
                          hoverYLabel="Rolling Power"
                          hoverYFormat=".1f"
                        />
                      ) : (
                        <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
                          <p>No data available for chart.</p>
                        </div>
                      )}
                    </div>
                  );
                })()}

                {/* 4a. Acceleration StdDev - Offset */}
                {hasAccelerationData && (() => {
                  const { scatterData, baselineInfo } =
                    createAccelerationOffsetPlotData();
                  return Object.keys(scatterData).length > 0 ? (
                    <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break">
                      <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                        Acceleration StdDev - Offset
                      </h3>
                      <TyreDeltaScatterChart
                        airspeedKph={baselineInfo.airspeedKph}
                        baselineTyreName={baselineInfo.tyreName}
                        baselinePressure={baselineInfo.pressure}
                        scatterData={scatterData}
                        pressureUnit={pressureUnit}
                        deltaAverage={deltaAverage}
                        equipmentCatalog={equipmentCatalog}
                        height={600}
                        groupBy="tyre"
                        showLogo={false}
                        lineFromPrimeOnly={true}
                        yAxisLabel={accelLabels.offsetAxis}
                        plotTitleMain={
                          'Acceleration StdDev - Offset'
                        }
                        plotTitleSub={accelLabels.offsetSub}
                        hoverYLabel={accelLabels.offsetAxis}
                        hoverYFormat={accelLabels.offsetFormat}
                      />
                    </div>
                  ) : null;
                })()}

                {/* 4b. Acceleration StdDev - Absolute */}
                {hasAccelerationData && (() => {
                  const { scatterData, baselineInfo } =
                    createAccelerationPlotData();
                  return Object.keys(scatterData).length > 0 ? (
                    <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break">
                      <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                        {accelLabels.absoluteTitle}
                      </h3>
                      <TyreDeltaScatterChart
                        airspeedKph={baselineInfo.airspeedKph}
                        baselineTyreName={baselineInfo.tyreName}
                        baselinePressure={baselineInfo.pressure}
                        scatterData={scatterData}
                        pressureUnit={pressureUnit}
                        deltaAverage={deltaAverage}
                        equipmentCatalog={equipmentCatalog}
                        height={600}
                        groupBy="tyre"
                        showLogo={false}
                        lineFromPrimeOnly={true}
                        yAxisLabel={accelLabels.absoluteAxis}
                        plotTitleMain={accelLabels.absoluteTitle}
                        plotTitleSub={accelLabels.absoluteSub}
                        hoverYLabel={accelLabels.absoluteHover}
                        hoverYFormat={accelLabels.absoluteFormat}
                      />
                    </div>
                  ) : null;
                })()}

                {/* 5. Rolling Power vs Accel StdDev */}
                {hasAccelerationData && (() => {
                  const { scatterData, baselineInfo } =
                    createAccelVsRollingPlotData();
                  const kph = baselineInfo.airspeedKph || 0;
                  if (Object.keys(scatterData).length === 0) {
                    return null;
                  }
                  const shared = {
                    airspeedKph: kph,
                    baselineTyreName: baselineInfo.tyreName,
                    baselinePressure: baselineInfo.pressure,
                    scatterData,
                    pressureUnit,
                    deltaAverage,
                    equipmentCatalog,
                    height: 600,
                    groupBy: 'tyre' as const,
                    showLogo: false,
                    showLines: false,
                    yAxisLabel: 'Rolling Power<br>(Watts)',
                    hoverYLabel: 'Rolling Power',
                    hoverYFormat: '.1f',
                    hoverXLabel: accelLabels.vsRollingHover,
                    hoverXFormat: accelLabels.vsRollingFormat,
                  };
                  const logScatterData = accelLogScatterData(scatterData);
                  return (
                    <>
                      <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break">
                        <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                          Rolling Power vs Accel StdDev - Linear
                        </h3>
                        <TyreDeltaScatterChart
                          {...shared}
                          xAxisLabel={accelLabels.vsRollingAxis}
                          plotTitleMain={
                            'Rolling Power vs Accel StdDev'
                            + ' - Linear Scale'
                          }
                          plotTitleSub={
                            `Normalised at ${kph} kph`
                          }
                        />
                      </div>
                      <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 print-avoid-break">
                        <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-4">
                          Rolling Power vs Accel StdDev - Log
                        </h3>
                        <TyreDeltaScatterChart
                          {...shared}
                          scatterData={logScatterData}
                          hoverXLabel={accelLabels.vsRollingLogHover}
                          xAxisLabel={accelLabels.vsRollingLogAxis}
                          xAxisType="log"
                          plotTitleMain={
                            'Rolling Power vs Accel StdDev'
                            + ' - Log Scale'
                          }
                          plotTitleSub={
                            `Normalised at ${kph} kph`
                          }
                        />
                      </div>
                    </>
                  );
                })()}
              </div>

              {/* Session Summary Table */}
              <div className="cr-bg-white cr-rounded-lg cr-shadow cr-overflow-hidden">
                <table className="cr-min-w-full cr-divide-y cr-divide-gray-200">
                  <thead className="cr-bg-gray-50">
                    <tr>
                      <th className="cr-px-6 cr-py-3 cr-text-left cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                        Date
                      </th>
                      <th className="cr-px-6 cr-py-3 cr-text-left cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                        Description
                      </th>
                      <th className="cr-px-6 cr-py-3 cr-text-left cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                        Surface Quality
                      </th>
                    </tr>
                  </thead>
                    {sessions.map((session: any, idx: number) => {
                      let surfaceDescription = 'Unknown';
                      if (session.surface_quality !== null && session.surface_quality !== undefined) {
                        try {
                          const surfaceQuality = SurfaceQuality.fromFloat(session.surface_quality);
                          surfaceDescription = surfaceQuality.getDescription();
                        } catch {
                          surfaceDescription = `Surface ${session.surface_quality}`;
                        }
                      }

                      // Collect unique tyres with their pressures and traverse counts from this session
                      type TyreConfig = { pressure: number; traverseCount: number };
                      type TyreMapValue = { name: string; width: number; configs: TyreConfig[] };
                      const tyreMap = new Map<string, TyreMapValue>();
                      session.configs?.forEach((config: any) => {
                        if (config.exclude_from_plot) return;
                        const tyreId = config.tyre_rear_id;
                        const pressure = config.tyre_rear_pressure;
                        if (!tyreId || !pressure) return;

                        // Get tyre info from equipment catalog
                        const tyreInfo = equipmentCatalog?.tyres?.find((t: any) => t.id === tyreId);
                        const tyreName = tyreInfo?.name || tyreInfo?.designation || `Tyre ${tyreId.slice(0, 8)}`;
                        const tyreWidth = tyreInfo?.width_mm || 0;

                        // Count traverses from all runs in this config
                        let traverseCount = 0;
                        config.runs?.forEach((run: any) => {
                          if (run.traverse?.['Crr_xG5']) {
                            traverseCount += run.traverse['Crr_xG5'].filter((v: any) => v !== null && v !== undefined).length;
                          }
                        });

                        if (!tyreMap.has(tyreId)) {
                          tyreMap.set(tyreId, { name: tyreName, width: tyreWidth, configs: [] });
                        }

                        // Check if this pressure already exists for this tyre
                        const existingConfig = tyreMap.get(tyreId)!.configs.find(c => c.pressure === pressure);
                        if (existingConfig) {
                          existingConfig.traverseCount += traverseCount;
                        } else {
                          tyreMap.get(tyreId)!.configs.push({ pressure, traverseCount });
                        }
                      });

                      // Sort tyres by width
                      const sortedTyres = Array.from(tyreMap.values()).sort((a, b) => a.width - b.width);

                      return (
                        <tbody key={session.id || idx} className="cr-bg-white cr-border-b cr-border-gray-200">
                          <tr>
                            <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-blue-900">
                              {session.date ? new Date(session.date).toLocaleDateString('en-CA') : 'N/A'}
                            </td>
                            <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-blue-900">
                              {session.description || session.name || 'Unnamed Session'}
                            </td>
                            <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-blue-900">
                              {surfaceDescription}
                            </td>
                          </tr>
                          {/* Sub-rows for each tyre */}
                          {sortedTyres.map((tyre, tyreIdx) => {
                            const sortedConfigs = tyre.configs.sort((a, b) => a.pressure - b.pressure);
                            const gateDistanceM = session.gates?.route_distance || 0;
                            return (
                              <tr key={tyreIdx} className="cr-bg-gray-50">
                                <td className="cr-px-6 cr-pl-10 cr-py-2 cr-text-sm cr-text-blue-900">
                                  <span className="cr-font-medium">{tyre.name}</span>
                                  {tyre.width > 0 && <span className="cr-text-blue-500"> ({tyre.width}mm)</span>}
                                </td>
                                <td colSpan={2} className="cr-px-6 cr-py-2 cr-text-sm cr-text-blue-900">
                                  {sortedConfigs.map((c, cIdx) => {
                                    const distanceKm = (c.traverseCount * gateDistanceM) / 1000;
                                    return (
                                      <span key={cIdx} className="cr-mr-6">
                                        {convertPressure(c.pressure, pressureUnit).toFixed(1)}{' '}
                                        {pressureUnit.toLowerCase()} - {distanceKm.toFixed(1)} km
                                      </span>
                                    );
                                  })}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      );
                    })}
                </table>
              </div>

              {/* Wheel Circumference Section */}
              <div id="section-wheel-circumference" className="print-page-break-before">
                <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mt-8 screen-only">Wheel Circumference</h3>
                <div className="print-only print-section-header">Wheel Circumference</div>
              </div>

              <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6">
                <p className="cr-text-sm cr-text-blue-900 cr-mb-4">
                  Wheel circumference vs pressure data for tyres used in these sessions
                </p>
                <TyreReportWheelCircumferencePlot
                  wheelCircumferenceData={wheelCircumferenceData}
                  tyres={equipmentCatalog?.tyres || []}
                  usedTyreIds={usedTyreIds}
                  height={500}
                />
              </div>

              {/* Tyre Compensation Section */}
              <div id="section-temperature-compensation" className="print-page-break-before">
                <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mt-8 screen-only">Tyre Temperature Compensation</h3>
                <div className="print-only print-section-header">Tyre Temperature Compensation</div>
              </div>

              <div className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6">
                <p className="cr-text-sm cr-text-blue-900 cr-mb-4">
                  Temperature compensation model from the reference session baseline configuration
                </p>
                <TyreCompensationPlot
                  session={sessions[0]}
                  height={400}
                />
              </div>

              {/* Individual Session Summarised Results Header */}
              <div id="section-individual-sessions" className="print-page-break-before">
                <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 screen-only">Individual Session Summarised Results</h3>
                <div className="print-only print-section-header">Individual Session Summarised Results</div>
              </div>

              {sessions.map((session, sessionIndex) => {
                // Determine which field to use based on deltaAverage setting
                const deltaField = deltaAverage === 'Moving' ? 'deltas_rolling' : 'deltas';

                // Build table data
                const tableData: Array<{
                  sessionName: string;
                  config: string;
                  tyreName: string;
                  pressure: string;
                  crr: number | null;
                  deltaWatts: number | null;
                  isBaseline: boolean;
                }> = [];

                session.configs?.forEach((config: any) => {
                  if (config.exclude_from_plot) return;
                  if (config.settings_collection?.exclude_tyre_report) return;

                  const tyrePressureBar = config.tyre_rear_pressure;
                  const tyreId = config.tyre_rear_id;

                  if (!tyrePressureBar || !tyreId) return;

                  const tyrePressure = convertPressure(tyrePressureBar, pressureUnit);
                  const tyreName = getCombinedTyreName(config.tyre_front_id, tyreId, equipmentCatalog);
                  const isBaseline = config.letter_number === 0;

                  // Get CRR from config.data_average
                  const crr = config.average?.Crr_xG5?.[0] ?? null;

                  // Get delta watts from config
                  let deltaWatts: number | null = null;
                  const deltaValue = config[deltaField]?.['RollingResistanceNormalised_PG5'];
                  if (deltaValue !== null && deltaValue !== undefined) {
                    deltaWatts = Array.isArray(deltaValue) ? deltaValue[0] : deltaValue;
                    deltaWatts = deltaWatts * speedScale; // Apply speed scaling
                  }

                  tableData.push({
                    sessionName: session.description || session.name || new Date(session.date).toLocaleDateString(),
                    config: config.letter || config.letter_number?.toString() || '',
                    tyreName,
                    pressure: `${tyrePressure.toFixed(1)} ${pressureUnit}`,
                    crr,
                    deltaWatts,
                    isBaseline,
                  });
                });

                // Get chart data
                const barPlotData = createBarPlotData(session);
                const baselineInfo = getBaselineInfo(session);

                return (
                  <div key={session.id || sessionIndex} className="cr-bg-white cr-rounded-lg cr-shadow print-avoid-break">
                    <div className="cr-px-6 cr-py-4 cr-border-b cr-border-gray-200">
                      <h2 className="cr-text-xl cr-font-semibold cr-text-blue-900">
                        {session.description || 'Session'} - {session?.date ? new Date(session.date).toLocaleDateString() : 'No date'}
                      </h2>
                    </div>

                    {/* Stacked layout: Table on top, Chart below */}
                    <div className="cr-flex cr-flex-col cr-gap-6 cr-p-6">
                      {/* Table Section */}
                      <div className="cr-overflow-x-auto">
                        <table className="cr-min-w-full cr-divide-y cr-divide-gray-200">
                          <thead className="cr-bg-gray-50">
                            <tr>
                              <th className="cr-px-6 cr-py-3 cr-text-left cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                                Config
                              </th>
                              <th className="cr-px-6 cr-py-3 cr-text-left cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                                Tyre
                              </th>
                              <th className="cr-px-6 cr-py-3 cr-text-center cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                                Pressure
                              </th>
                              <th className="cr-px-6 cr-py-3 cr-text-center cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                                Coefficient of Rolling Resistance
                              </th>
                              <th className="cr-px-6 cr-py-3 cr-text-center cr-text-sm cr-font-medium cr-text-blue-900 cr-uppercase cr-tracking-wider">
                                Watts vs Baseline
                              </th>
                            </tr>
                          </thead>
                          <tbody className="cr-bg-white cr-divide-y cr-divide-gray-200">
                            {tableData.length === 0 ? (
                              <tr>
                                <td colSpan={5} className="cr-px-6 cr-py-4 cr-text-center cr-text-blue-900">
                                  No data available for this session
                                </td>
                              </tr>
                            ) : (
                              tableData.map((row, rowIndex) => (
                                <tr key={rowIndex} className={row.isBaseline ? 'cr-bg-blue-50' : ''}>
                                  <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-font-medium cr-text-blue-900">
                                    {row.config}
                                    {row.isBaseline && (
                                      <span className="cr-ml-2 cr-text-sm cr-text-blue-600 cr-font-semibold">(Baseline)</span>
                                    )}
                                  </td>
                                  <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-blue-900">
                                    {row.tyreName}
                                  </td>
                                  <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-blue-900 cr-text-center">
                                    {row.pressure}
                                  </td>
                                  <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-blue-900 cr-text-center">
                                    {row.crr !== null ? row.crr.toFixed(5) : 'N/A'}
                                  </td>
                                  <td className="cr-px-6 cr-py-4 cr-whitespace-nowrap cr-text-base cr-text-center">
                                    {row.deltaWatts !== null ? (
                                      <span
                                        className={
                                          row.deltaWatts < 0
                                            ? 'cr-text-green-600 cr-font-medium'
                                            : row.deltaWatts > 0
                                            ? 'cr-text-red-600 cr-font-medium'
                                            : 'cr-text-blue-900'
                                        }
                                      >
                                        {row.deltaWatts >= 0 ? '+' : ''}
                                        {row.deltaWatts.toFixed(1)} W
                                      </span>
                                    ) : (
                                      'N/A'
                                    )}
                                  </td>
                                </tr>
                              ))
                            )}
                          </tbody>
                        </table>
                      </div>

                      {/* Chart Section */}
                      <div className="cr-pt-4 cr-border-t cr-border-gray-200">
                        {Object.keys(barPlotData.subplotData).length > 0 ? (
                          <TyreDeltaBarChartApex
                            sessionDate={session?.date}
                            sessionDescription={session?.description}
                            airspeedKph={baselineInfo.airspeedKph}
                            riderMassKg={session?.rider_mass}
                            airTemperatureC={session?.air_temperature}
                            baselineTyreName={baselineInfo.tyreName}
                            baselinePressure={baselineInfo.pressure}
                            subplotData={barPlotData.subplotData}
                            pressureUnit={pressureUnit}
                            deltaAverage={deltaAverage}
                            equipmentCatalog={equipmentCatalog}
                            height={500}
                          />
                        ) : (
                          <div className="cr-flex cr-h-64 cr-w-full cr-items-center cr-justify-center cr-text-blue-900">
                            <p>No chart data available</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Session Conditions and Detailed Run Data Header */}
              <div id="section-detailed-data" className="print-page-break-before">
                <h3 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mt-8 screen-only">Session Conditions and Detailed Run Data</h3>
                <div className="print-only print-section-header">Session Conditions and Detailed Run Data</div>
              </div>

              {/* Session Conditions for each session */}
              {sessions.map((session: any, sessionIdx: number) => {
                // Get surface quality description
                let surfaceDescription = 'Unknown';
                if (session.surface_quality !== null && session.surface_quality !== undefined) {
                  try {
                    const surfaceQuality = SurfaceQuality.fromFloat(session.surface_quality);
                    surfaceDescription = surfaceQuality.getDescription();
                  } catch {
                    surfaceDescription = `Surface ${session.surface_quality}`;
                  }
                }

                // Get rider name
                const riderName = session?.rider_id && ridersById?.get(session.rider_id)?.nickname || 'Unknown Rider';

                // Get meteorological source selection
                const selectionMap: Record<string, string> = {
                  weather_api: 'Weather Api',
                  probe: 'Probe',
                  probe_hybrid: 'Probe-API',
                  local_station: 'Local Weather Station',
                  manual: 'Manual',
                  bike_computer: 'Bike Computer',
                };
                const meteorologicalSource = session?.weather_controller_dict?.selection
                  ? selectionMap[session.weather_controller_dict.selection] || 'Weather Api'
                  : 'Weather Api';

                const route = session?.gates?.route;
                const gates = session?.gates;

                return (
                  <div key={session.id || sessionIdx} className="cr-bg-white cr-rounded-lg cr-shadow cr-p-6 cr-mb-6 print-page-break-before">
                    {/* Session Header Info */}
                    <div className="cr-mb-4">
                      <h4 className="cr-text-lg cr-font-semibold cr-text-blue-900 cr-mb-2">
                        {session.date ? new Date(session.date).toLocaleDateString('en-CA') : 'N/A'} - {riderName} - {session.description || 'No description'}
                      </h4>
                      {(() => {
                        // Try route.route_address first, fall back to gates.address
                        const addressSource = route?.route_address?.Label ? route.route_address : gates?.address;
                        if (!addressSource) return null;

                        const label = addressSource.Label || '';
                        const locality = addressSource.Locality || '';
                        const district = addressSource.District || '';

                        const locationParts = [label];
                        if (locality && !label.includes(locality)) {
                          locationParts.push(locality);
                        }
                        if (district && !label.includes(district) && !locality.includes(district)) {
                          locationParts.push(district);
                        }
                        const locationString = locationParts.filter(Boolean).join(', ');

                        if (!locationString) return null;

                        return (
                          <p className="cr-text-sm cr-text-blue-900">
                            <span className="cr-font-medium">Location:</span> {locationString}
                          </p>
                        );
                      })()}
                      <p className="cr-text-sm cr-text-blue-900">
                        <span className="cr-font-medium">Surface Quality:</span> {surfaceDescription}
                      </p>
                    </div>

                    {/* Course Map and Elevation Profile */}
                    <div className="cr-mb-16 cr-pb-6 cr-border-b cr-border-gray-200">
                      <h5 className="cr-text-md cr-font-medium cr-text-blue-900 cr-mb-3">Course</h5>
                      {route ? (
                        <div className="cr-grid cr-grid-cols-1 lg:cr-grid-cols-2 cr-gap-4">
                          <CourseMapPlot route={route} gates={gates} />
                          <ElevationProfilePlot route={route} gates={gates} height={300} />
                        </div>
                      ) : (
                        <div className="cr-text-blue-900 cr-text-sm">No route data available</div>
                      )}
                    </div>

                    {/* Weather */}
                    <div>
                      <h5 className="cr-text-md cr-font-medium cr-text-blue-900 cr-mb-3">Weather</h5>
                      {session?.weather_controller_dict || session?.configs ? (
                        <div className="cr-grid cr-grid-cols-1 lg:cr-grid-cols-3 cr-gap-4">
                          <WeatherTemperaturePlot
                            weatherControllerDict={session.weather_controller_dict}
                            selectedSource={meteorologicalSource}
                            utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
                            height={300}
                          />
                          <WindSpeedPlot
                            configs={session.configs || []}
                            utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
                            weatherControllerDict={session?.weather_controller_dict || null}
                            height={300}
                          />
                          <WindDirectionPlot
                            configs={session.configs || []}
                            utcOffsetSeconds={Number(session?.utc_offset ?? 0)}
                            weatherControllerDict={session?.weather_controller_dict || null}
                            height={300}
                          />
                        </div>
                      ) : (
                        <div className="cr-text-blue-900 cr-text-sm">No weather data available</div>
                      )}
                    </div>

                    {/* Crr Plots - left out of the Session Details export */}
                    <div className="cr-mt-6" data-export-scope="full">
                      <h5 className="cr-text-md cr-font-medium cr-text-blue-900 cr-mb-3">Crr Plots</h5>
                      <div className="cr-flex cr-flex-col cr-gap-6 cr-w-full">
                        <div className="cr-w-full">
                          <CrrRawTimeOfDayPlot
                            session={session}
                            equipmentCatalog={equipmentCatalog}
                            pressureUnit={pressureUnit}
                            height={450}
                          />
                        </div>
                        <div className="cr-w-full">
                          <CrrTimeOfDayPlot
                            session={session}
                            equipmentCatalog={equipmentCatalog}
                            pressureUnit={pressureUnit}
                            height={450}
                          />
                        </div>
                        <div className="cr-w-full">
                          <CrrDeltaTimeOfDayPlot
                            session={session}
                            equipmentCatalog={equipmentCatalog}
                            pressureUnit={pressureUnit}
                            deltaAverage={deltaAverage}
                            height={450}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Detailed Run Data for this session */}
                    {session?.configs && (
                      <div className="cr-mt-6">
                        <h5 className="cr-text-md cr-font-medium cr-text-gray-700 cr-mb-3">Detailed Run Data</h5>
                        {/* Session notes - only show if present */}
                        {session.notes && session.notes.length > 0 && (
                          <div className="cr-mb-4 cr-bg-gray-50 cr-rounded cr-p-3">
                            <h6 className="cr-text-sm cr-font-semibold cr-text-gray-700 cr-mb-1">Notes</h6>
                            <div
                              className="cr-text-sm cr-text-gray-600"
                              dangerouslySetInnerHTML={{ __html: session.notes }}
                            />
                          </div>
                        )}
                        <PrintDataTable
                          rows={buildPrintDataRows(session.configs)}
                          columnsPage1={getPrintDataColumnsTyrePage1(
                            session?.airspeed_ref ? Math.round(session.airspeed_ref * 3.6) : 100,
                            session?.temperature_ref ?? 20
                          )}
                          columnsPage2={PRINT_DATA_COLUMNS_TYRE_PAGE2}
                          titlePage1="Detailed Run Data - Crr & Power"
                          titlePage2="Detailed Run Data - Speed, Position & Calibration"
                          getCellValue={getStandardCellValue}
                          className="screen-data-table"
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* No Data State */}
          {!isLoading && selectedSessionId && (!sessions || sessions.length === 0) && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
              <p>No session data found for the selected reference session.</p>
            </div>
          )}

          {/* No Selection State */}
          {!selectedSessionId && (
            <div className="cr-flex cr-h-64 cr-items-center cr-justify-center cr-text-blue-900">
              <p>Please select a reference session to view the report.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
